'use client';

import AudioPlayer from '@/components/component_unique/AudioPlayer';
import TweetEmbed from '@/components/component_unique/TweetEmbed';
import InstagramEmbed from '@/components/component_unique/InstagramEmbed';
import VideoEmbed from '@/components/component_unique/VideoEmbed';
import GumletEmbed from '@/components/component_unique/GumletEmbed';
import ChipsForm from '@/components/component_form/ChipsForm';
import JustIn from '@/components/component_unique/JustIn';
import VuukleComments from '@/components/ads/VuukleComments';
import YouTubeThumbnail from '@/components/component_unique/YouTubeThumbnail';
import FileAttachment from '@/components/component_unique/FileAttachment';
import PhotoCarousel from '@/components/component_unique/PhotoCarousel';
import { getImageUrl } from '@/lib/api';
import { processArticleLinks } from '@/lib/linkUtils';
import { sanitizeToText } from '@/lib/sanitize';
import { computeParagraphMap, ParagraphInfo } from '@/lib/articleInlineAds';

/**
 * A Q/A card's text always renders as one un-split block, so it should never
 * show more than one ad even if it internally spans several paragraph-map
 * indices (a long answer with multiple <p> tags). Returns the first
 * non-null ad found among the card's owned indices, or null.
 */
function renderFirstInlineAd(renderInlineAd: ((globalIdx: number) => React.ReactNode) | undefined, paraInfo: ParagraphInfo | undefined): React.ReactNode {
  if (!renderInlineAd || !paraInfo) return null;
  for (let paraIdx = 0; paraIdx < paraInfo.paragraphs.length; paraIdx++) {
    const ad = renderInlineAd(paraInfo.startIndex + paraIdx);
    if (ad) return ad;
  }
  return null;
}
export interface ArticleInterviewBodyProps {
  story: any;
  index: number;
  activeAdArticleIndex: number;
  category: string;
  imageUrl: string;
  updatedAt: string;
  showAudioPlayer: boolean;
  promotionBlock: React.ReactNode;
  renderInlineAd?: (globalIdx: number, kind?: 'text' | 'image') => React.ReactNode;
  showRelatedStory?: boolean;
  showPromotionalMessage?: boolean;
  showLinkedStories?: boolean;
  showStoryTags?: boolean;
  showComments?: boolean;
  /** Whether to render this component's own author/updated-on line below the
   *  hero. The dynamic article-template renderer already shows author +
   *  dateMeta via ArticleHeroSection, so it passes showHero={false} —
   *  same convention as ArticleTextBody/ArticleQABody. */
  showHero?: boolean;
}

/**
 * The `interview` article body — extracted from the `story['story-template']
 * === 'interview'` branch in src/app/api/articles/ArticleListInfinite.tsx.
 * The defining feature vs. the generic text body and the Q&A body: elements
 * with `subtype === 'question'` render in a "Q" badge / light-blue card,
 * elements with `subtype === 'answer'` render in an "A" badge / light-cream
 * card — a third, distinct question/answer visual language.
 */
export default function ArticleInterviewBody({
  story,
  index,
  activeAdArticleIndex,
  category,
  imageUrl,
  updatedAt,
  showAudioPlayer,
  promotionBlock,
  renderInlineAd,
  showRelatedStory = true,
  showPromotionalMessage = true,
  showLinkedStories = true,
  showStoryTags = true,
  showComments = true,
  showHero = true
}: ArticleInterviewBodyProps) {
  const tagNames = story.tags?.map((tag: any) => ({
    name: tag.name,
    slug: tag.slug
  }));
  const inlineAlsoReadIds = new Set<string>();
  story.cards?.forEach((card: any) => {
    card['story-elements']?.forEach((el: any) => {
      if (el.subtype === 'also-read') {
        const id = el.metadata?.['linked-story-id'] || el['linked-story-id'];
        if (id) inlineAlsoReadIds.add(id);
      }
    });
  });
  return <>
      {/* No author/date/share row here at all: all three are already
          covered by ArticleHeroSection's authorBlock/dateMeta/socialShare
          fields — duplicating them in the body just shows the same
          info/icons twice on the page. */}
      {promotionBlock}
      {showAudioPlayer && <AudioPlayer />}
      {story['embed-js'] && <div className="border-b border-DCDCDC p-4">
          {(() => {
        try {
          const decoded = atob(story['embed-js']);
          if (decoded.includes('twitter-tweet') || decoded.includes('platform.twitter.com')) {
            return <TweetEmbed embedJs={story['embed-js']} />;
          }
          if (decoded.includes('instagram-media') || decoded.includes('instagram.com')) {
            return <InstagramEmbed embedJs={story['embed-js']} />;
          }
        } catch {
          if (story['embed-js'].includes('twitter-tweet') || story['embed-js'].includes('platform.twitter.com')) {
            return <TweetEmbed embedJs={story['embed-js']} />;
          }
          if (story['embed-js'].includes('instagram-media') || story['embed-js'].includes('instagram.com')) {
            return <InstagramEmbed embedJs={story['embed-js']} />;
          }
        }
        return <GumletEmbed embedJs={story['embed-js']} />;
      })()}
        </div>}
      {story['embed-url'] && <div className="border-b border-DCDCDC">
          <YouTubeThumbnail embedUrl={story['embed-url']} thumbnailUrl={imageUrl} title={story.headline} />
        </div>}
      <div className="p-4 space-y-6">
        {/* afterParagraph: 0 means "before the first paragraph" — see buildParagraphAdPlan. */}
        {renderInlineAd?.(-1)}
        {(() => {
        const paraMap = computeParagraphMap(story);
        return story.cards?.map((card_0: any, cardIdx: number) => <div key={cardIdx}>
              {card_0['story-elements']?.filter((el_0: any) => el_0 && (el_0.text || el_0.type || el_0.subtype)).map((element: any, elementIdx: number) => {
            const paraInfo = paraMap[`${cardIdx}_${elementIdx}`];
            const isQuestion = element.subtype === 'question' && !!element.text;
            const isAnswer = element.subtype === 'answer' && !!element.text;
            const isDefaultText = element.text && element.type !== 'jsembed' && element.type !== 'youtube-video' && element.subtype !== 'question' && element.subtype !== 'answer' && element.subtype !== 'also-read' && element.subtype !== 'summary' && element.subtype !== 'quote' && element.subtype !== 'blockquote' && element.subtype !== 'cta' && element.subtype !== 'q-and-a' && element.subtype !== 'bigfact' && element.subtype !== 'blurb' && !element.metadata?.['promotional-message'];
            return <div key={elementIdx} className="mb-6">
                    {element.type === 'jsembed' && element.subtype === 'tweet' && <TweetEmbed embedJs={element['embed-js']} />}
                    {element.type === 'jsembed' && element.subtype === 'instagram' && <InstagramEmbed embedJs={element['embed-js']} />}
                    {element.type === 'jsembed' && element.subtype !== 'tweet' && element.subtype !== 'instagram' && <VideoEmbed embedJs={element['embed-js']} type={element.type} subtype={element.subtype} />}
                    {element.type === 'youtube-video' && element['embed-url'] && <YouTubeThumbnail embedUrl={element['embed-url']} thumbnailUrl={imageUrl} title={element.title} />}
                    {isQuestion ? <>
                        {/* Question Design - Light Blue Background */}
                        <div style={{
                  backgroundColor: '#E3F2FD',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                          <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                            <span style={{
                      backgroundColor: '#1565C0',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>Q</span>
                            <div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-3 [&_p:last-child]:mb-0" style={{
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: processArticleLinks(element.text)
                    }} />
                          </div>
                        </div>
                        {/* afterParagraph is keyed by the global paragraph index
                            computeParagraphMap assigns to every text-bearing
                            element regardless of subtype — a question/answer
                            element still "owns" one or more of those indices
                            (a long answer can span several), so an ad slot can
                            land here even though the Q/A card renders as a
                            single block instead of being split per-paragraph
                            like the default design. Render at most ONE ad for
                            this card — the card's text isn't split visually,
                            so firing renderInlineAd once per owned index would
                            stack multiple ads back-to-back with no paragraph
                            content between them whenever a long Q/A card
                            happens to own more than one configured position. */}
                        {renderFirstInlineAd(renderInlineAd, paraInfo)}
                      </> : isAnswer ? <>
                        {/* Answer Design - Light Cream/Beige Background */}
                        <div style={{
                  backgroundColor: '#FFF3E0',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                          <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                            <span style={{
                      backgroundColor: '#E65100',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>A</span>
                            <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-3 [&_p:last-child]:mb-0" style={{
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: processArticleLinks(element.text)
                    }} />
                          </div>
                        </div>
                        {renderFirstInlineAd(renderInlineAd, paraInfo)}
                      </> : <div className="space-y-4">
                        {element.subtype === 'summary' && element.text && <div>
                            <h3 className="text-18-tamil-700 text-1B1B1B mb-2">Summary</h3>
                            <div className="text-18-tamil-400 text-1B1B1B mb-6 article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />
                          </div>}
                        {element.subtype === 'quote' && element.text && <div className="my-4 quote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
                  __html: processArticleLinks(element.text)
                }} />}
                        {element.subtype === 'blockquote' && element.text && <div className="my-4 blockquote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
                  __html: processArticleLinks(element.text)
                }} />}
                        {isDefaultText && paraInfo && paraInfo.paragraphs.length > 0 && paraInfo.paragraphs.map((paraHtml: string, paraIdx: number) => {
                  const globalIdx = paraInfo.startIndex + paraIdx;
                  return <div key={`p-${paraIdx}`}>
                                <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                      __html: processArticleLinks(paraHtml)
                    }} />
                                {renderInlineAd?.(globalIdx)}
                              </div>;
                })}
                        {isDefaultText && (!paraInfo || paraInfo.paragraphs.length === 0) && <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                  __html: processArticleLinks(element.text)
                }} />}
                      </div>}
                    {element.text && element.metadata?.['promotional-message'] && showPromotionalMessage && <div className="text-14-tamil-400 article-links [&_p]:mb-4" style={{
                color: '#00000099'
              }} dangerouslySetInnerHTML={{
                __html: processArticleLinks(element.text)
              }} />}
                    {element.type === 'file' && element['content-type'] !== 'application/pdf' && element.url && element['file-name'] && <div className="mb-4">
                        <FileAttachment fileName={element['file-name']} url={element.url} fileSize={element.metadata?.['file-size']} />
                      </div>}
                    {element.type === 'composite' && element.subtype === 'image-gallery' && element['story-elements']?.length > 0 && (element.metadata?.type === 'slideshow' ? <div className="my-4">
                          <PhotoCarousel photos={element['story-elements'].filter((img: any) => img['image-s3-key']).map((img_0: any) => ({
                  id: img_0.id,
                  title: img_0.title,
                  imageS3Key: img_0['image-s3-key'],
                  imageMetadata: img_0['image-metadata'],
                  attribution: img_0['image-attribution']
                }))} maxHeight={400} />
                        </div> : <div className="my-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {element['story-elements'].map((img_1: any, imgIdx: number) => img_1['image-s3-key'] && <div key={img_1.id || imgIdx}>
                              <img src={getImageUrl(img_1['image-s3-key'])} alt={img_1['alt-text'] || img_1.title || 'Gallery image'} className="w-full h-[150px] object-cover rounded" />
                              {img_1['image-attribution'] && <p className="text-12-tamil-400 mt-1" style={{
                    color: '#00000099'
                  }}>{sanitizeToText(img_1['image-attribution'])}</p>}
                            </div>)}
                        </div>)}
                    {element.type === 'composite' && element.subtype === 'references' && element['story-elements']?.length > 0 && <div className="my-4 border border-DCDCDC rounded-lg p-4 bg-gray-50">
                        <h4 className="text-16-tamil-700 text-1B1B1B mb-2">குறிப்புகள்</h4>
                        <ul className="space-y-2 list-disc pl-5">
                          {element['story-elements'].map((ref: any, refIdx: number) => <li key={ref.id || refIdx} className="text-14-tamil-400 text-1B1B1B">
                              {ref.metadata?.url ? <a href={ref.metadata.url} target="_blank" rel="noopener noreferrer" className="text-012061 hover:underline">
                                  {ref.metadata?.name || 'Reference'}
                                </a> : <span>{ref.metadata?.name}</span>}
                              {ref.metadata?.description && <span className="block text-12-tamil-400" style={{
                      color: '#00000099'
                    }}>{ref.metadata.description}</span>}
                            </li>)}
                        </ul>
                      </div>}
                    {element.subtype === 'cta' && element.metadata?.['cta-url'] && <div className="my-4 text-center">
                        <a href={element.metadata['cta-url']} target={element.metadata['open-in-new-tab'] ? '_blank' : '_self'} rel="noopener noreferrer" className="inline-block px-6 py-3 bg-012061 text-white rounded-full text-16-tamil-600 hover:opacity-90 transition-opacity">
                          {element.metadata['cta-title'] || element.text}
                        </a>
                      </div>}
                    {element.subtype === 'q-and-a' && (element.metadata?.question || element.metadata?.answer) && <div className="my-4 space-y-3">
                        {element.metadata?.question && <div style={{
                  backgroundColor: '#E3F2FD',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                            <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                              <span style={{
                      backgroundColor: '#1565C0',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>Q</span>
                              <div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: processArticleLinks(element.metadata.question)
                    }} />
                            </div>
                          </div>}
                        {element.metadata?.answer && <div style={{
                  backgroundColor: '#FFF3E0',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                            <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                              <span style={{
                      backgroundColor: '#E65100',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>A</span>
                              <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-0" style={{
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: processArticleLinks(element.metadata.answer)
                    }} />
                            </div>
                          </div>}
                      </div>}
                    {element.subtype === 'bigfact' && (element.metadata?.content || element.text) && <div className="my-6 text-center border-y border-DCDCDC py-6">
                        <div className="text-32-tamil-700 text-012061">{element.metadata?.content}</div>
                        {element.metadata?.attribution && <div className="mt-2 text-14-tamil-400" style={{
                  color: '#00000099'
                }}>{element.metadata.attribution}</div>}
                      </div>}
                    {element.subtype === 'blurb' && element.text && <div className="my-4 bg-gray-50 rounded-lg p-4 article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-600 [&_blockquote]:italic [&_blockquote]:text-1B1B1B" dangerouslySetInnerHTML={{
                __html: processArticleLinks(element.text)
              }} />}
                    {element.subtype === 'also-read' && element.text && (() => {
                const linkedStoryId = element.metadata?.['linked-story-id'] || element['linked-story-id'];
                const linkedStory = linkedStoryId && story['linked-stories']?.[linkedStoryId] || element.metadata?.['linked-story'];
                if (linkedStory && linkedStory.slug) {
                  return <a href={`/${linkedStory.slug}`} className="flex flex-col sm:flex-row gap-3 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow my-3 p-4">
                            {linkedStory['hero-image-s3-key'] && <div className="w-full sm:w-[150px] h-[160px] sm:h-[100px] flex-shrink-0">
                                <img src={getImageUrl(linkedStory['hero-image-s3-key'])} alt={linkedStory.headline} className="w-full h-full object-cover" />
                              </div>}
                            <div className="p-3 flex-1 flex items-center">
                              <h4 className="text-16-tamil-700 text-1B1B1B">
                                {linkedStory.headline}
                              </h4>
                            </div>
                          </a>;
                }
                return null;
              })()}
                  </div>;
          })}
            </div>);
      })()}

        {showLinkedStories && story['linked-stories'] && Object.keys(story['linked-stories']).length > 0 && <div className="mb-6">
            <div className="grid grid-cols-1 gap-4">
              {Object.entries(story['linked-stories']).filter(([key]) => !inlineAlsoReadIds.has(key)).map(([key_0, linkedStory_0]: [string, any]) => <a key={key_0} href={`/${linkedStory_0.slug}`} className="flex flex-col lg:flex-row gap-3 lg:gap-4 p-4 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow">
                  {linkedStory_0['hero-image-s3-key'] && <div className="w-full lg:w-[200px] h-[200px] lg:h-[120px] flex-shrink-0">
                      <img src={getImageUrl(linkedStory_0['hero-image-s3-key'])} alt={linkedStory_0.headline} className="w-full h-full object-cover" />
                    </div>}
                  <div className="p-3 lg:p-4 flex-1 flex items-center">
                    <h4 className="text-16-tamil-700 lg:text-18-tamil-700 text-1B1B1B">
                      {linkedStory_0.headline}
                    </h4>
                  </div>
                </a>)}
            </div>
          </div>}
      </div>
      {index === 0 && showRelatedStory && <JustIn limit={5} />}
      {showStoryTags && tagNames && tagNames.length > 0 && <div className="border-b border-DCDCDC"><ChipsForm chips={tagNames} /></div>}
      {showComments && Math.abs(index - activeAdArticleIndex) <= 1 && <div style={{
      display: index === activeAdArticleIndex ? 'block' : 'none'
    }} aria-hidden={index !== activeAdArticleIndex}>
          <p style={{
        fontSize: '12px',
        color: '#666',
        lineHeight: 1.6,
        padding: '12px',
        background: '#f9f9f9',
        borderRadius: '6px',
        borderLeft: '3px solid #012061',
        margin: '12px 16px'
      }}>பின்னூட்டத்தில் வெளியாகும் கருத்துகளுக்கு அவற்றைப் பதிவிடுவோரே முழுப் பொறுப்பு; அவை தினமணியின் கருத்துகளைப் பிரதிபலிக்கவில்லை.தனிநபர், சமூகம், மதம் அல்லது நாடு ஆகியவற்றுக்கு எதிராக அவமதிக்கிற அல்லது ஆபாசமான விதத்திலுள்ள எந்தவொரு கருத்தும் இந்திய அரசின் தகவல் தொழில்நுட்பக் கொள்கைப்படி தண்டனைக்குரிய குற்றம். இதுபோன்ற கருத்துகளுக்கு எதிராக உரிய சட்ட நடவடிக்கை எடுக்கப்படும்.</p>

          <div className="border-b border-DCDCDC p-4">
            <VuukleComments key={`vuukle-${story.id}`} articleId={story.id} articleTitle={story.headline || ''} articleUrl={`https://www.dinamani.com/${story.slug}`} articleImage={imageUrl} articleTags={story.tags?.slice(0, 3).map((tag_0: any) => tag_0.name).join(',') || category} articleAuthor={story.authors?.[0]?.name || 'Dinamani'} />
          </div>
        </div>}
    </>;
}